import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import customFetchBase from '../expiredToken/expiredToken';

const customFetch = customFetchBase(config.baseUrlCustomers);

export interface PartNumber {
  id: number;
  partNumber: string;
  series: string;
  product: string;
  model: string;
  sku: string;
  startSerialNumber: string;
  description: string;
}

export interface SerialNumberEntry {
  id: number;
  partNumberId: number;
  serialNumber: string;
  manufacturingDate: string;
  partNumber: PartNumber;
  selected?: boolean;
}

export type PartNumberData = {
  id: number;
  partNumber: string;
  product: string;
  series: string;
  model: string;
  description: string;
};

export const apiManufacture = createApi({
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getPartNumber: builder.mutation<PartNumberData[], null>({
      query: () => ({
        url: `api/Manufacturing/PartNumbers`,
        method: 'GET'
      })
    }),
    deletePartNumber: builder.mutation<null, number>({
      query: (partNumberId) => ({
        url: `api/Manufacturing/PartNumber/${partNumberId}`,
        method: 'DELETE'
      })
    }),
    createPartNumber: builder.mutation<null, any>({
      query: (payload) => ({
        url: 'api/Manufacturing/PartNumber',
        body: { ...payload },
        method: 'POST'
      })
    }),
    updatePartNumber: builder.mutation<null, any>({
      query: (payload) => ({
        url: 'api/Manufacturing/PartNumber',
        body: { ...payload },
        method: 'PUT'
      })
    }),
    getSerialNumber: builder.mutation<any[], null>({
      query: () => ({
        url: `api/Manufacturing/SerialNumbers`,
        method: 'GET'
      })
    }),
    deleteSerialNumber: builder.mutation<null, number>({
      query: (partNumberId) => ({
        url: `api/Manufacturing/SerialNumber/${partNumberId}`,
        method: 'DELETE'
      })
    }),
    createSerialNumber: builder.mutation<null, any>({
      query: (payload) => ({
        url: 'api/Manufacturing/SerialNumbers',
        body: { ...payload },
        method: 'POST'
      })
    }),
    updateSerialNumber: builder.mutation<null, any>({
      query: (payload) => ({
        url: 'api/Manufacturing/SerialNumbers',
        body: { ...payload },
        method: 'PUT'
      })
    }),
    getSerialNumberSummary: builder.mutation<null, any>({
      query: () => ({
        url: `api/Manufacturing/SerialNumbers/Summary`,
        method: 'GET'
      })
    }),
    getShippingOrder: builder.mutation<null, any>({
      query: () => ({
        url: `api/Manufacturing/ShippingOrders`,
        method: 'GET'
      })
    }),
    deleteShippingOrder: builder.mutation<null, number>({
      query: (shippingOrderId) => ({
        url: `api/Manufacturing/ShippingOrder/${shippingOrderId}`,
        method: 'DELETE'
      })
    }),
    getSerialNumberByRange: builder.mutation<
      SerialNumberEntry[],
      { startSerial: string; endSerial: string }
    >({
      query: ({ startSerial, endSerial }) => ({
        url: `api/Manufacturing/SerialNumber/SerialRange?startSerial=${startSerial}&endSerial=${endSerial}`,
        method: 'GET'
      })
    }),
    addShippingOrder: builder.mutation<
      null,
      {
        soNumber: string;
        ShippingDate: string;
        SerialNumbers: string[];
      }
    >({
      query: ({ soNumber, ShippingDate, SerialNumbers }) => ({
        url: 'api/Manufacturing/ShippingOrder',
        method: 'POST',
        body: { soNumber, ShippingDate, SerialNumbers }
      })
    }),
    getShippingOrderDetails: builder.mutation<
      null,
      { shippingOrderId: number }
    >({
      query: ({ shippingOrderId }) => ({
        url: `api/Manufacturing/ShippingOrder/${shippingOrderId}`,
        method: 'GET'
      })
    })
  })
});

export const {
  useGetPartNumberMutation,
  useCreatePartNumberMutation,
  useDeletePartNumberMutation,
  useUpdatePartNumberMutation,
  useGetSerialNumberMutation,
  useCreateSerialNumberMutation,
  useUpdateSerialNumberMutation,
  useDeleteSerialNumberMutation,
  useGetSerialNumberSummaryMutation,
  useGetShippingOrderMutation,
  useDeleteShippingOrderMutation,
  useGetSerialNumberByRangeMutation,
  useAddShippingOrderMutation,
  useGetShippingOrderDetailsMutation
} = apiManufacture;
